<template>
  <div>
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-bold text-gray-900">Categorias</h1>
      <button
        @click="showAddModal = true"
        class="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium"
      >
        + Nova Categoria
      </button>
    </div>
    
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ordem</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="category in categories" :key="category.id" class="hover:bg-gray-50">
            <td class="px-6 py-4 font-medium text-gray-900">{{ category.name }}</td>
            <td class="px-6 py-4 text-gray-500">{{ category.description || '-' }}</td>
            <td class="px-6 py-4">{{ category.sort_order }}</td>
            <td class="px-6 py-4">
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" 
                :class="category.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
                {{ category.is_active ? 'Ativo' : 'Inativo' }}
              </span>
            </td>
            <td class="px-6 py-4">
              <button @click="editCategory(category)" class="text-blue-600 hover:text-blue-800 font-medium mr-3">
                Editar
              </button>
              <button @click="toggleActive(category)" class="text-gray-600 hover:text-gray-800">
                {{ category.is_active ? 'Desativar' : 'Ativar' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      
      <div v-if="categories.length === 0" class="p-8 text-center text-gray-500">
        Nenhuma categoria cadastrada
      </div>
    </div>
    
    <!-- Add/Edit Category Modal -->
    <div v-if="showAddModal || showEditModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="bg-white rounded-xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <h2 class="text-xl font-bold mb-4">{{ editingCategory ? 'Editar Categoria' : 'Nova Categoria' }}</h2>
        
        <form @submit.prevent="editingCategory ? updateCategory() : addCategory()">
          <div class="space-y-4">
            <!-- Image Upload -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Imagem da categoria</label>
              <div class="space-y-2">
                <!-- Preview -->
                <div v-if="imagePreview || categoryForm.image_url" class="relative w-full h-40 rounded-lg overflow-hidden bg-gray-100">
                  <img 
                    :src="imagePreview || getImageUrl(categoryForm.image_url)" 
                    class="w-full h-full object-cover"
                    alt="Preview"
                  >
                  <button
                    type="button"
                    @click="removeImage"
                    class="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 text-xs"
                  >
                    ✕
                  </button>
                </div>
                <!-- Upload Input -->
                <div v-else class="w-full h-40 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-orange-400 transition-colors" @click="triggerImageUpload">
                  <svg class="w-10 h-10 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span class="text-sm text-gray-500">Clique para enviar imagem</span>
                </div>
                <input
                  ref="imageInput"
                  type="file"
                  accept="image/*"
                  class="hidden"
                  @change="handleImageChange"
                >
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Nome</label>
              <input v-model="categoryForm.name" type="text" required class="w-full border rounded-lg px-3 py-2">
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
              <textarea v-model="categoryForm.description" class="w-full border rounded-lg px-3 py-2" rows="2"></textarea>
            </div>
            
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Ordem</label>
                <input v-model.number="categoryForm.sort_order" type="number" class="w-full border rounded-lg px-3 py-2">
              </div>
              <div class="flex items-center">
                <label class="flex items-center cursor-pointer mt-6">
                  <input v-model="categoryForm.is_active" type="checkbox" class="mr-2">
                  <span class="text-sm">Ativo</span>
                </label>
              </div>
            </div>
          </div>
          
          <div class="flex justify-end gap-3 mt-6">
            <button type="button" @click="closeModal" class="px-4 py-2 text-gray-600 hover:text-gray-800">
              Cancelar
            </button>
            <button type="submit" class="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700">
              {{ editingCategory ? 'Atualizar' : 'Salvar' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useAuthStore } from '@/stores'
import { categoryApi } from '@/utils/api'
import { notifyError, notifySuccess } from '@/utils/notifications'
import type { Category } from '@/types'

const authStore = useAuthStore()

const categories = ref<Category[]>([])
const showAddModal = ref(false)
const showEditModal = ref(false)
const editingCategory = ref<Category | null>(null)

const categoryForm = ref({
  name: '',
  description: '',
  sort_order: 0,
  is_active: true,
  image_url: ''
})

const imageFile = ref<File | null>(null)
const imagePreview = ref('')
const imageInput = ref<HTMLInputElement | null>(null)

const getImageUrl = (url: string | undefined) => {
  if (!url) return ''
  if (url.startsWith('http')) return url
  return url
}

const triggerImageUpload = () => {
  imageInput.value?.click()
}

const handleImageChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files[0]) {
    const file = target.files[0]
    imageFile.value = file
    imagePreview.value = URL.createObjectURL(file)
  }
}

const removeImage = () => {
  imageFile.value = null
  imagePreview.value = ''
  categoryForm.value.image_url = ''
  if (imageInput.value) {
    imageInput.value.value = ''
  }
}

const loadCategories = async () => {
  const companyId = authStore.companyId || 1
  try {
    const res = await categoryApi.getByCompanyAdmin(companyId)
    categories.value = res.data
  } catch (error) {
    console.error('Erro ao carregar categorias:', error)
  }
}

const closeModal = () => {
  showAddModal.value = false
  showEditModal.value = false
  editingCategory.value = null
  categoryForm.value = { name: '', description: '', sort_order: 0, is_active: true, image_url: '' }
  imageFile.value = null
  imagePreview.value = ''
}

const editCategory = (category: Category) => {
  editingCategory.value = category
  categoryForm.value = {
    name: category.name,
    description: category.description || '',
    sort_order: category.sort_order,
    is_active: category.is_active,
    image_url: category.image_url || ''
  }
  imageFile.value = null
  imagePreview.value = ''
  showEditModal.value = true
}

const addCategory = async () => {
  const companyId = authStore.companyId || 1
  try {
    const res = await categoryApi.create({
      ...categoryForm.value,
      company_id: companyId
    })
    const newCategoryId = res.data.id
    
    // Upload image if selected
    if (imageFile.value && newCategoryId) {
      const formData = new FormData()
      formData.append('image', imageFile.value)
      await categoryApi.uploadImage(newCategoryId, formData)
    }
    
    closeModal()
    loadCategories()
    notifySuccess('Categoria criada com sucesso!')
  } catch (error: any) {
    notifyError(error.response?.data?.detail || 'Erro ao criar categoria')
  }
}

const updateCategory = async () => {
  if (!editingCategory.value) return
  
  try {
    await categoryApi.update(editingCategory.value.id, categoryForm.value)
    
    // Upload new image if selected
    if (imageFile.value) {
      const formData = new FormData()
      formData.append('image', imageFile.value)
      await categoryApi.uploadImage(editingCategory.value.id, formData)
    }
    
    closeModal()
    loadCategories()
    notifySuccess('Categoria atualizada com sucesso!')
  } catch (error: any) {
    notifyError(error.response?.data?.detail || 'Erro ao atualizar categoria')
  }
}

const toggleActive = async (category: Category) => {
  try {
    await categoryApi.update(category.id, { is_active: !category.is_active })
    category.is_active = !category.is_active
  } catch (error) {
    notifyError('Erro ao atualizar status')
  }
}

onMounted(() => {
  loadCategories()
})
</script>
